/*
 * sm-facets-cards.js — spec rows on filtered result cards.  2026-08-07
 *
 * WHAT THIS FIXES
 * ---------------
 * Dawn's server-rendered collection card carries three specifications on its
 * face — DIN level, cut type, sheet capacity — because that is the actual job
 * on a collection page in this category (see snippets/sm-card-spec-rows.liquid).
 * The cards assets/sm-facets.js renders when a spec filter is active carried
 * none of them: vendor, title, price only. So the moment a visitor used the
 * filters — the one moment they have told us exactly which specifications they
 * care about — every card stopped showing those specifications.
 *
 * The data was already there. sm-facets.js indexes sl (security level), ct
 * (cut type) and sc (sheet capacity) on every product in order to filter on
 * them; SmFacets.prototype.card simply never printed them.
 *
 * WHY A PATCH RATHER THAN AN EDIT
 * -------------------------------
 * sm-facets.js is a 34KB filtering engine full of hand-validated regular
 * expressions, and faceted filtering is the highest-risk thing on this
 * template to break. Replacing one method from outside touches nothing else in
 * it: if this file fails to load or the internals ever change shape, the
 * guards below simply do nothing and the original card renderer stays in
 * place. Filtering itself cannot be affected either way.
 *
 * HOW IT ATTACHES
 * ---------------
 * sm-facets.js exposes its instance as window.smFacets after DOMContentLoaded,
 * and SmFacets.prototype.renderResults calls `this.card`, so assigning an own
 * property on the instance shadows the prototype method cleanly. `card` is
 * invoked through Array.prototype.map with no thisArg, so the replacement — like
 * the original — never uses `this`.
 *
 * This script is loaded from sections/sm-facets.liquid immediately after
 * sm-facets.js, both deferred, so it executes second. It still polls for the
 * instance rather than assuming, because the instance is created inside a
 * DOMContentLoaded handler whose ordering is an implementation detail of that
 * file, not a contract. The poll gives up after ~3 seconds.
 *
 * The markup it emits is the same .smf-card__spec* structure assets/
 * sm-facets.css already styles, which is the same treatment as .sm-spec-rows
 * on the Liquid card and .smh-specs on the homepage.
 */
(function () {
  'use strict';

  if (window.__smFacetCardsReady) return;
  window.__smFacetCardsReady = true;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function clean(s) {
    return String(s == null ? '' : s).replace(/<[^>]*>/g, '').trim();
  }

  /* Sheet capacity trimmed to its leading figure. This is the same rule as
     `split: '(' | first | strip | remove_first: 'Up to ' | strip` in
     snippets/sm-card-spec-rows.liquid, so a filtered card and an unfiltered
     card can never print different numbers for the same product. */
  function shortCapacity(s) {
    var t = clean(s).split('(')[0].trim();
    if (t.slice(0, 6).toLowerCase() === 'up to ') t = t.slice(6).trim();
    if (t.length > 26) t = t.slice(0, 25).trim() + '…';
    return t;
  }

  function row(key, value) {
    if (!value) return '';
    return '<span class="smf-card__spec">' +
      '<span class="smf-card__spec-k">' + esc(key) + '</span>' +
      '<span class="smf-card__spec-v">' + esc(value) + '</span>' +
      '</span>';
  }

  function specs(it) {
    var rows = row('DIN level', clean(it.sl)) +
               row('Cut', clean(it.ct)) +
               row('Capacity', shortCapacity(it.sc));
    return rows ? '<span class="smf-card__specs">' + rows + '</span>' : '';
  }

  /* Deliberately identical to SmFacets.prototype.card apart from one inserted
     line, so the two stay easy to compare. */
  function card(it) {
    var img = it.img
      ? '<img class="smf-card__img" src="' + esc(it.img) + '" alt="' + esc(it.alt) +
        '" loading="lazy" width="400" height="400">'
      : '<span class="smf-card__img" aria-hidden="true"></span>';
    return '<li class="smf-card">' +
      '<a class="smf-card__link" href="' + esc(it.u) + '">' +
      '<span class="smf-card__media">' + img + '</span>' +
      '<span class="smf-card__body">' +
      (it.v ? '<span class="smf-card__vendor">' + esc(it.v) + '</span>' : '') +
      '<span class="smf-card__title">' + esc(it.t) + '</span>' +
      specs(it) +
      '<span class="smf-card__price">' + esc(it.pm) +
      (it.cap ? ' <s class="smf-card__was">' + esc(it.cap) + '</s>' : '') +
      '</span></span></a></li>';
  }

  var tries = 0;

  function attach() {
    var f = window.smFacets;

    if (!f || typeof f.renderResults !== 'function') {
      if (++tries > 180) return;          /* ~3s at 60fps, then give up quietly */
      window.requestAnimationFrame(attach);
      return;
    }

    f.card = card;

    /* If a shared or bookmarked filtered URL already painted the grid before
       this ran, repaint it once. renderResults is idempotent and reads no
       state this file has touched. */
    try {
      if (f.els && f.els.results && !f.els.results.hidden) f.renderResults();
    } catch (e) {
      /* Never let a repaint failure be worse than the missing rows. */
      if (window.console) console.warn('[sm-facets-cards] repaint skipped', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
